import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

export interface ChatRoomContextValue {
  chatRoomUuid: string;
  isMessaging: boolean;
}

const ChatRoomContext = createContext<ChatRoomContextValue>({
  chatRoomUuid: "main",
  isMessaging: false,
});

ChatRoomContext.displayName = "ChatRoomContext";

export interface ChatRoomContextProviderProps {
  chatRoomUuid: string;
  children: ReactNode;
  isMessaging?: boolean;
}

export const ChatRoomContextProvider = ({
  chatRoomUuid,
  isMessaging = false,
  children,
}: ChatRoomContextProviderProps) => {
  // Memoized: every chat message row consumes this context, so a new value
  // identity would re-render all (memoized) rows.
  const value = useMemo(
    () => ({ chatRoomUuid, isMessaging }),
    [chatRoomUuid, isMessaging],
  );

  return (
    <ChatRoomContext.Provider value={value}>
      {children}
    </ChatRoomContext.Provider>
  );
};

export const useChatRoomContext = () => useContext(ChatRoomContext);
